import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector, { type CustomDetector } from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';
import da from './locales/da.json';
import no from './locales/no.json';
import de from './locales/de.json';
import fr from './locales/fr.json';

/**
 * Supported UI languages. English is the source language and fallback.
 * Norwegian uses the `no` macro-language code.
 */
export const SUPPORTED_LANGUAGES = ['en', 'fi', 'sv', 'da', 'no', 'de', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/** localStorage key used to persist an explicit user language choice. */
export const LANGUAGE_STORAGE_KEY = 'identity_ui_language';

const resources = {
  en: { translation: en },
  fi: { translation: fi },
  sv: { translation: sv },
  da: { translation: da },
  no: { translation: no },
  de: { translation: de },
  fr: { translation: fr },
} as const;

/**
 * Normalize an arbitrary locale tag to one of our supported languages.
 * Handles region subtags (`sv-SE` -> `sv`), Norwegian variants
 * (`nb`/`nn` -> `no`), and returns null when nothing matches.
 */
export const normalizeLanguage = (value: string | null | undefined): SupportedLanguage | null => {
  if (!value) {
    return null;
  }

  const base = value.trim().toLowerCase().split(/[-_]/)[0];

  if (base === 'nb' || base === 'nn' || base === 'nob' || base === 'nno') {
    return 'no';
  }

  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : null;
};

/**
 * Custom detector that reads an explicit locale request from the URL.
 * Supports both `?lang=` and the OIDC-style `?ui_locales=` (a space- or
 * comma-separated, ordered preference list). The first supported value wins.
 */
const urlLanguageDetector: CustomDetector = {
  name: 'urlLanguage',
  lookup() {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    const candidates = [
      params.get('lang'),
      ...((params.get('ui_locales') ?? '').split(/[\s,]+/)),
    ];

    for (const candidate of candidates) {
      const normalized = normalizeLanguage(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return undefined;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(urlLanguageDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    fallbackLng: DEFAULT_LANGUAGE,
    // Treat `sv-SE`, `en-US`, etc. as their base language.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      // Priority: explicit URL request -> saved choice -> browser language.
      order: ['urlLanguage', 'localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes values, so i18next must not double-escape.
      escapeValue: false,
    },
    returnNull: false,
  });

// Expose the instance for debugging / translation-completeness checks in dev.
// Guarded so it never leaks onto the global scope in a production bundle.
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as { i18n: typeof i18n }).i18n = i18n;
}

export default i18n;
